import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '~Styles';
import PageButton from './Components/PageButton';
import NoAccess from './Components/NoAccess';
import PrinterSettings from './Scenes/Printers';
import PosLayoutSettings from './Scenes/PosLayout';
import PaymentsSettings from './Scenes/Payments';
import KDSSettings from './Scenes/KDS';
import KitchenTicketsSettings from './Scenes/KitchenTickets';
import styled from '@emotion/styled';
import OtherSettings from './Scenes/Other';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import Icon from '~ComponentsImproved/Icon';
import LanguageIcon from '~Assets/icons/language.svg';
import { useButton, useI18n, useLicenses, useLogger, useSelector, useStaffCan } from '~Hooks';
import { ModalContent } from '~Styles/modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import { Languages } from '~Types';
import DeveloperPage from './Scenes/Developer';
import SafTPage from './Scenes/SafT';
import EjournalPage from './Scenes/Ejournal';
import { useHistory, useLocation } from 'react-router-dom';
import StaffSettings from './Scenes/StaffSettings';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import AppSettings from './App';
import BongMaster from '~Scenes/Tables/Components/BongMaster';
import ReceiptsSettings from './Scenes/Receipts';
import TablesSettings from './Scenes/Tables';
import BlueprintsSettings from './Scenes/Blueprints';

const Home = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

/** Handheld home: the page buttons stacked as rows in one block, like HandySettingsBlock */
const HandyPageList = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: var(--colorBlueGray1100);
  overflow: hidden;
  margin-top: 12px;
`;

const Wrapper = styled.div<{ isHandy: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  height: ${({ isHandy }) => (isHandy ? 'auto' : 'calc(100vh - 64px)')};
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
`;

const RightWrapper = styled.div`
  margin-right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
`;

const InnerWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const LanguageButton = styled.div<{ touching: boolean }>`
  margin-right: 8px;
  transform: ${({ touching }) => (touching ? 'scale(0.96)' : '1')};
`;

export { default as SettingsBlock } from './Components/SettingsBlock';

export const SettingsLoaderWrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  padding: 48px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  overflow: hidden;
`;

const LanguageToggle = styled.div<{ active: boolean }>`
  height: 64px;
  padding: 8px;
  width: 144px;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 4px;
  border: ${({ active }) =>
    active ? '2px solid var(--colorWhite)' : '2px solid var(--colorBlueGray900)'};
  cursor: pointer;
  transition: all 0.15s ease-out;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  letter-spacing: 0.24px;
  font-size: 16px;
  color: ${({ active }) => (active ? 'var(--colorWhite)' : 'var(--colorBlueGray400)')};

  &:active {
    transform: scale(0.94);
  }
`;

const ScrollWrapper = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
  flex: 1;
  margin-top: 4px;
`;

export const pages = [
  'printers',
  'kitchen-tickets',
  'KDS',
  'layout',
  'payments',
  'receipts',
  'tables',
  'staff-settings',
  'app',
  'safT',
  'ejournal',
  'blueprints',
  'other',
] as const;

export type PageButtonType = typeof pages[number];
export type PageType = typeof pages[number] | 'developer';

interface Props {
  isHandy: boolean;
  /** Handheld navigates through Handy's PageProvider, so the shell animates and handles back */
  onNavigateHandy?: (page: PageType) => void;
}

const Settings: FC<Props> = ({ isHandy, onNavigateHandy }) => {
  const languageModal = useModal();
  const locale = useSelector((state) => state.locale);
  const [selectedLanguage, setSelectedLanguage] = useState<Languages>(locale);
  const country = useSelector((state) => state.country);
  const { changeLanguage, i18n } = useI18n();
  const logger = useLogger('settings-v2');
  const { pathname } = useLocation();

  const page = pathname.split('/').pop();
  const licenses = useLicenses();

  const staffId = useSelector((state) => state.staff?.id);
  const staffCan = useStaffCan();
  const venueId = useSelector((state) => state.venueId);
  const companyStaffs = useSelector((state) => state.companyStaff);

  const history = useHistory();

  useEffect(() => {
    setSelectedLanguage(locale);
  }, [locale]);

  // Stable reference so the memoized PageButtons don't re-render on every Settings render
  const navigateToPage = useCallback(
    (page: PageType | null) => {
      // On handheld the router can't be used: pushing `settings-v2/...` would
      // leave the `/handy` route. Hand over to Handy's PageProvider instead.
      if (isHandy) {
        if (page) {
          onNavigateHandy?.(page);
          logger.info(`Navigated to ${page}`);
        }
        return;
      }

      if (!page) {
        history.goBack();
        return;
      }

      history.push({
        pathname: `settings-v2/${page}`,
      });

      logger.info(`Navigated to ${page}`);
    },
    [history, isHandy, onNavigateHandy],
  );

  const handleChangeLanguage = (loc: Languages) => {
    setSelectedLanguage(loc);
    changeLanguage(loc);
    logger.info(`Changed language to ${loc}`);
  };

  const PAGE_TITLES = useMemo<Record<PageType, string>>(
    () => ({
      printers: i18n('Settings.Printers'),
      layout: i18n('Settings.PosLayout'),
      payments: i18n('Settings.Payments'),
      receipts: i18n('Settings.Receipts'),
      KDS: i18n('Settings.KdsSettings'),
      'kitchen-tickets': i18n('Settings.KitchenTickets'),
      tables: i18n('Settings.Tables'),
      blueprints: i18n('Settings.Blueprints'),
      safT: 'Saf-T',
      other: i18n('Settings.Other'),
      developer: 'Developer',
      ejournal: i18n('Settings.EJournal'),
      'staff-settings': i18n('Settings.StaffSettings'),
      app: i18n('Settings.AppSettings'),
    }),
    [i18n],
  );

  const filteredPages = useMemo(() => {
    const hasStaff = companyStaffs && companyStaffs?.staffs?.length > 0;

    return pages.filter((page) => {
      // KDS, the app and the e-journal are not configured from a handheld
      if (isHandy && (page === 'KDS' || page === 'app' || page === 'ejournal')) return false;
      if (page === 'app') return licenses.hasApp;
      if (page === 'staff-settings') return hasStaff;
      if (page === 'ejournal') return country === 'SE';
      if (page === 'safT') return country === 'NO';
      return true;
    });
  }, [country, licenses.hasApp, companyStaffs?.staffs, isHandy]);

  const pageToShow = useMemo(() => {
    switch (page) {
      case 'printers':
        return <PrinterSettings />;

      case 'layout':
        return (
          <InnerWrapper>
            <PosLayoutSettings />
          </InnerWrapper>
        );

      case 'payments':
        return (
          <InnerWrapper>
            <PaymentsSettings />
          </InnerWrapper>
        );

      case 'receipts':
        return (
          <InnerWrapper>
            <ReceiptsSettings />
          </InnerWrapper>
        );

      case 'KDS':
        return (
          <InnerWrapper>
            <KDSSettings />
          </InnerWrapper>
        );

      case 'kitchen-tickets':
        return (
          <InnerWrapper>
            <KitchenTicketsSettings />
          </InnerWrapper>
        );

      case 'tables':
        return (
          <InnerWrapper>
            <TablesSettings />
          </InnerWrapper>
        );

      case 'blueprints':
        return (
          <InnerWrapper>
            <BlueprintsSettings />
          </InnerWrapper>
        );

      case 'staff-settings':
        return (
          <InnerWrapper>
            <StaffSettings />
          </InnerWrapper>
        );

      case 'other':
        return (
          <InnerWrapper>
            <OtherSettings setSelectedPage={navigateToPage} />
          </InnerWrapper>
        );

      case 'safT':
        return (
          <InnerWrapper>
            <SafTPage />
          </InnerWrapper>
        );

      case 'ejournal':
        return (
          <InnerWrapper>
            <EjournalPage />
          </InnerWrapper>
        );

      case 'app':
        return (
          <InnerWrapper>
            <AppSettings />
          </InnerWrapper>
        );

      case 'developer':
        return <DeveloperPage goBack={() => navigateToPage(null)} />;

      default:
        return (
          <Home>
            {isHandy ? (
              <HandyPageList>
                {filteredPages.map((page) => (
                  <PageButton key={page} page={page} setSelectedPage={navigateToPage} isHandy />
                ))}
              </HandyPageList>
            ) : (
              <Box padding="xl">
                <Box
                  display="flex"
                  gap="large"
                  flexWrap="wrap"
                  alignItems="center"
                  justifyContent="center">
                  {filteredPages.map((page) => (
                    <PageButton key={page} page={page} setSelectedPage={navigateToPage} />
                  ))}
                </Box>
                {/* TODO: hide until we have real questions */}
                {/* TODO: add logger for folding out of questions */}
                {/* <InnerWrapper>
                <FAQ />
              </InnerWrapper> */}
              </Box>
            )}
          </Home>
        );
    }
  }, [page, filteredPages, isHandy, navigateToPage]);

  const languageButton = useButton({
    onClick: languageModal.toggle,
  });

  const title =
    page && page in PAGE_TITLES ? PAGE_TITLES[page as PageType] : i18n('Settings.Title');

  const noAccess = <NoAccess isHandy={isHandy} />;

  // Handheld only renders the page list; the Handy shell provides title, back
  // navigation and the slide transition through its PageProvider
  if (isHandy) {
    return staffCan.SETTINGS ? pageToShow : noAccess;
  }

  return (
    <>
      <Wrapper isHandy={false}>
        <BackTopBar title={title} goBack={() => history.goBack()} dark>
          <RightWrapper>
            <LanguageButton {...languageButton}>
              <Icon src={LanguageIcon} height="32px" width="32px" />
            </LanguageButton>
          </RightWrapper>
        </BackTopBar>

        <ScrollWrapper id="settings-scroll-wrapper">
          {/* If no staff show login modal, if the company don't use staff it will not show */}
          {!staffId && <LoginModal />}

          {staffCan.SETTINGS ? pageToShow : noAccess}
        </ScrollWrapper>
      </Wrapper>

      {/* Tablet only */}
      <Modal isShowing={languageModal.isShowing} hide={languageModal.toggle}>
        <ModalContent>
          <Box
            padding="xl"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center">
            <Heading level={1} color="var(--colorWhite)" bottom={40}>
              {i18n('Settings.SelectLanguage')}
            </Heading>
            <Box
              display="flex"
              gap="small"
              flexWrap="wrap"
              justifyContent="center"
              alignItems="center">
              <LanguageToggle
                onClick={() => handleChangeLanguage('en')}
                active={selectedLanguage === 'en'}>
                English
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('se')}
                active={selectedLanguage === 'se'}>
                Svenska
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('es')}
                active={selectedLanguage === 'es'}>
                Español
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('no')}
                active={selectedLanguage === 'no'}>
                Norsk
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('dk')}
                active={selectedLanguage === 'dk'}>
                Dansk
              </LanguageToggle>
              <LanguageToggle
                onClick={() => handleChangeLanguage('tr')}
                active={selectedLanguage === 'tr'}>
                Türkçe
              </LanguageToggle>
            </Box>
          </Box>
        </ModalContent>
      </Modal>

      {/* Bong master is here to be able to print auto accepted app orders */}
      <BongMaster />
    </>
  );
};

export default memo(Settings);
